
import { useState, useEffect } from 'react';

interface Artist {
  id: string;
  name: string;
  avatar: string;
}

interface Track {
  title: string;
  url: string;
  duration: number;
}

interface StickyPreviewBarProps {
  artist: Artist;
  track: Track;
  isPlaying: boolean;
  currentTime: number;
  onPlayPause: () => void;
  onClose: () => void;
}

export default function StickyPreviewBar({
  artist,
  track,
  isPlaying,
  currentTime,
  onPlayPause,
  onClose
}: StickyPreviewBarProps) {
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(track.duration);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPlaying) {
      interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (1 / track.duration) * 100;
          if (newProgress >= 100) {
            onClose();
            return 0;
          }
          return newProgress;
        });
        
        setTimeLeft(prev => {
          const newTimeLeft = prev - 1;
          if (newTimeLeft <= 0) {
            onClose();
            return track.duration;
          }
          return newTimeLeft;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, track.duration, onClose]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Track Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0 w-full sm:w-auto">
            <img
              src={artist.avatar}
              alt={artist.name}
              className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 whitespace-normal sm:truncate">
                {track.title}
              </p>
              <p className="text-xs text-gray-500 whitespace-normal sm:truncate">
                {artist.name}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:flex-nowrap sm:gap-4">
            <button
              onClick={onPlayPause}
              className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
            >
              {isPlaying ? (
                <i className="ri-pause-fill text-white text-lg"></i>
              ) : (
                <i className="ri-play-fill text-white text-lg ml-0.5"></i>
              )}
            </button>

            {/* Progress */}
            <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto sm:flex-1 sm:max-w-xs">
              <div className="flex-1 bg-gray-200 rounded-full h-1 min-w-[120px]">
                <div
                  className="bg-emerald-500 h-1 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatTime(timeLeft)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <button className="px-3 py-1.5 text-sm font-medium text-emerald-600 border border-emerald-600 rounded-md hover:bg-emerald-50 transition-colors whitespace-nowrap">
                Buy
              </button>
              <button className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap">
                Tip
              </button>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors ml-auto sm:ml-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
